import argTypes from 'utils/arg-types';
import { IntakeSerializedDateFormat, IntakeFilterAnswerType, IntakeFilterAnswer, IntakeDate } from './intakeTypes';

import moment from 'moment';
import { castArray } from 'lodash';

import { momentFormatForExtJSDateFormat } from 'utils/time';
import { trimTrailingZeros } from 'utils/text';

const dateArg = argTypes('toDate', IntakeDate.isRequired);

const parseDate = (dateOrString) => moment(dateOrString, IntakeSerializedDateFormat);

const toDate = dateArg(dateOrString =>
  parseDate(dateOrString).format(
    momentFormatForExtJSDateFormat(sfTenant.language.getDateFormat('dateDisplayFormat'))));

const toMonthYear = dateArg(dateOrString =>
  parseDate(dateOrString).format(
    momentFormatForExtJSDateFormat(sfTenant.language.getDateFormat('monthYearDisplayFormat'))));

const formatDateRange = ({start, end}) => `${toDate(start)} to ${toDate(end)}`;
const formatMonthRange = ({start, end}) => `${toMonthYear(start)} to ${toMonthYear(end)}`;

const operatorDisplayLabels = {
  '=': 'equal to',
  '<=': 'less than or equal to',
  '>=': 'greater than or equal to'
};

const operatorDisplayText = (operator) => operatorDisplayLabels[operator] || operator;

const formatNumberComparison = ({number, operator}) => `${operatorDisplayText(operator)} ${trimTrailingZeros(number)}`;

const commaSeparatedItems = (items) => {
  if (!items || items.length === 0) {
    return '';
  }

  if (items.length === 1) {
    return items[0];
  }

  const listFinalSeparator = (itemCount) => itemCount > 2 ? ', or ' : ' or ';
  return (
    items.slice(0, items.length - 1).join(', ')
    + listFinalSeparator(items.length)
    + items[items.length - 1]
  );
};

const intakeFilterFormatters = {
  // yes_no([boolean1, ...]) formats a boolean selection mapping false => No and true => Yes for display
  yes_no: values => commaSeparatedItems(castArray(values).map(boolean => boolean ? 'Yes' : 'No')),

  // full_date({start: DATE1, end: DATE2}) formats a date range as 'DATE1 to DATE2',
  // applying the Starfish tenant's dateDisplayFormat (which usually produces dates
  // such as "03-27-2014"). Dates may be supplied as moment objects, or as strings in
  // the format IntakeSerializedDateFormat (YYYY-MM-DD).
  full_date: formatDateRange,

  // month_year({start: DATE1, end: DATE2}) formats a date range as 'DATE1 to DATE2',
  // applying the Starfish tenant's monthYearDisplayFormat date format (which usually
  // produces dates such as "March 2014"). Dates may be supplied as moment objects, or
  // as strings in the format IntakeSerializedDateFormat (YYYY-MM-DD).
  month_year: formatMonthRange,

  // decimal(value) formats a value => { number: X, operator: OP } as `OP X`. `OP` will
  // be transformed to a readable name where possible.
  //
  // Examples:
  //   decimal({number: 3, operator: '<='}) => 'less than or equal to 3'
  //   decimal({number: '5', operator: '^'}) => '^ 5'
  //
  decimal: formatNumberComparison,

  // any(values) formats values as a set of comma-separated items. If `values` is not an array,
  // it is treated as the single-element array: `[values]`. All values are converted to strings
  // for display. The supplied list of values is not modified.
  //
  // Examples:
  //   any([1, 2, 3]) => '1, 2, or 3'
  //   any(['red', 'white']) => 'red or white'
  //   any(6) => '6'
  //
  any: values => commaSeparatedItems(castArray(values).map(value => value.toString()))
};

const intakeFilterArgs =
  argTypes('intakeFilterDisplayValue',
    IntakeFilterAnswerType.isRequired, IntakeFilterAnswer.isRequired);

const intakeFilterDisplayValue = intakeFilterArgs((answerType, answerValue) => {
  const answerTypeFormatter = intakeFilterFormatters[answerType.toLowerCase()] || intakeFilterFormatters.any;
  return answerTypeFormatter(answerValue);
});

export default intakeFilterDisplayValue;
